<template>
  <div class="flex flex-col gap-8">
    <div class="text-center">
      <h4 class="text-md font-bold">Quick Menu</h4>
    </div>
    
    <div class="flex flex-col gap-5">
      <div v-for="(item, index) in menu" :key="index">
        <div 
          class="flex flex-row justify-between gap-4 items-center cursor-pointer"
          @click="item.submenu ? toggleSubmenu(index) : navigate(item.href)"
        >
          <V3ImageComponent :src="item.icon" class="h-6 w-6"/>
          <div class="grow">{{ item.title }}</div>
          <V3ImageComponent v-if="item.submenu" src="icons/chevron-down.png" class="h-5 w-5"
            :class="{ 'rotate-180': openSubmenu === index }"/>
          <V3ImageComponent v-else src="icons/chevron-right.png" class="h-8 w-8"/>
        </div>
        
        <div v-if="openSubmenu === index" class="pl-6 mt-2 transition-all duration-300">
          <div v-for="(sub, subIndex) in item.submenu" :key="subIndex" class="py-2">
            <div @click="navigate(sub.href)" class="text-gray-600 hover:text-black cursor-pointer flex flex-row gap-4 items-center ">
              <V3ImageComponent :src="sub.icon" class="h-5 w-5"/>
              {{ sub.title }}
            </div>
            <UDivider class="py-2"  v-if="subIndex !== item.submenu.length - 1" />
          </div>
        </div>

        <UDivider class="pt-3 pb-1" v-if="index !== menu.length - 1"/>
      </div>

      <UButton block size="lg" class="my-2" v-if="authToken" @click="logout">Logout</UButton>

      <div v-if="loading" class="inline-flex items-center justify-center text-gray-500 mt-2">
        <p>Loading version...</p>
      </div>
      <div v-else-if="version" class="inline-flex items-center justify-center text-gray-500 mt-2">
        <V3ImageComponent src="icons/nstore.png" class="h-5 w-5 cursor-pointer" @click="navigateExternal()" />
        <p class="text-sm font-bold mt-1">{{ version.version_number }}</p>
      </div>
    </div>
  </div>
</template>


<script setup>
import { ref, onBeforeMount } from 'vue';
import { V3PopupComponent } from '#components';
import { V3ModalComponent } from '#components';
import { popupBackHandler } from '@/composables/popupBackHandler'
const runtimeConfig = useRuntimeConfig();
const partnerCode = runtimeConfig.public.partnerCode

const menu = ref([
  {
    title: 'My profile',
    icon: 'icons/order.png',
    href: '/account/profile'
  }, {
    title: 'My saved addresses',
    icon: 'icons/saved_address.png',
    href: '/account/addresses'
  }, {
    title: 'My orders',
    icon: 'icons/order.png',
    href: '/account/orders'
  },{
    title: 'My tickets',
    icon: 'icons/order.png', 
    href: '/account/tickets'
  },
  {
    title: 'Help & Support',
    icon: 'icons/h&s.png',
    href: '/support',
    submenu: [
      {
        title: 'Frequently Asked Questions (FAQs)',
        icon:'icons/faq.png',
        href: '/support/faq' 
      },
      { 
        title: 'Raise a Request',
        icon:'icons/raise_a_request.png',
        href: '/support/raise-request-page' 
      },
      { 
        title: 'Contact us',
        icon:'icons/contact.png',
        href: '/support/contact' 
      }
    ]
  }
]);

if (partnerCode === "mall4all") {
  menu.value.push({
    title: 'About Hamara Mall',
    icon: 'icons/aboutus.png',
    href: '/about',
    submenu: [
      { 
        title: 'About Us',
        icon:'icons/mall4allabout.png',
        href: '/about/aboutUs' 
      },
      { 
        title: 'Terms and Conditions',
        icon:'icons/t&c.png',
        href: '/about/terms' 
      },
      { 
        title: 'Privacy Policy',
        icon:'icons/privacy.png',
        href: '/about/privacy-policy' 
      }
    ]
  });
}
const openSubmenu = ref(null); 

const toggleSubmenu = (index) => {
  openSubmenu.value = openSubmenu.value === index ? null : index;
};
const navigateExternal = () => {
    window.location.href = 'https://www.nstore.in/';
};
const { gtag } = useGtag();
const auth = useAuth();
  const isAuthenticated = auth && auth.access_token;
  const userData = isAuthenticated ? {
    user_name: auth.user.first_name,
    user_phone: auth.user.phone,
    user_email: auth.user.email,
  } : {
    user_name: null,
    user_phone: null,
    user_email: null
  };
const slideover = useSlideover();
const modal = useModal();
const authToken = computed(() => auth.access_token);  
const authUser = computed(() => auth.user);
const { isPopupOpen, openPopup, closePopup } = popupBackHandler() 

const { isMobile } = useDevice();
const navigate = (href) => {
  const lastValue = href.split('/').pop();
  gtag('event', 'cta_event', {
    action: 'click',
    cta_name: 'quick menu',
    actionName: lastValue,
    ...userData,
  });
  if (['/account/profile', '/account/addresses', '/account/orders' , '/account/tickets'].includes(href) && !auth.access_token) {
    const options = {
      component: 'login',
      onSuccess: () => {  
        useSSOAuth(href);
      }
    }
    if (isMobile) {
      slideover.open(V3PopupComponent, { ...options, onClose: () => {slideover.close();}});
      // openPopup();
    } else {
      modal.open(V3ModalComponent, { ...options, onClose: () => {modal.close();}});
      // openPopup();
    }
    return;
  }
  return useSSOAuth(href);
}

const emit = defineEmits(['close']);
const logout = async () => {
  gtag('event', 'cta_event', {
    action: 'click',
    cta_name: 'logout',
    ...userData,
  });
  localStorage.removeItem("appliedCoupon");
  auth.$reset();
  useReward().$reset();
  emit('close');
  await useSSOAuth('/');
}

const version = ref(null);
const loading = ref(true);

onBeforeMount(async () => {
  try {
    const response = await useAPIHandler('get', '/account/getVersion');
    if (response.data) {
      version.value = response.data;
    } else {
      version.value = null;
    }
  } catch (error) {
    console.error("Error fetching version data:", error);
    version.value = null;
  }finally {
    loading.value = false; 
  }
});

gtag('event', 'cta_event', {
    action: 'click',
    cta_name: 'header profile icon',
    ...userData,
  });

// watch(isPopupOpen, (newVal) => {
//   if(!newVal) {
//    slideover.close();
//    modal.close();
//   }
// })
</script>
